// RBAC permission vocabulary - the shared core.
//
// SOURCE OF RECORD: "Code Word - RBAC Matrix.xlsx" (tabs "RBAC Matrix",
// "Roles", "Rules & Decisions"), transcribed 2026-08-18. If this file and the
// workbook disagree, the workbook wins and this file is wrong.
//
// THIS FILE EXISTS TWICE, ON PURPOSE. The frontend (CodeWord-Front-NEW-deploy)
// and the gateway (CodeWord-Back-NEW) each carry a byte-identical copy at
// src/constants/rbac.ts. The two repos share no package mechanism, separate CI,
// and separate deploys; a workspace package would be new build infrastructure
// for one file. The parity test in each repo hashes the canonical
// serialization (rbacChecksumInput below) and asserts it against
// RBAC_PARITY_SHA256 - a mismatch means the two copies drifted, and both files
// must be re-synced (and the hash updated in BOTH) before either change ships.
//
// WORKBOOK RULES THAT BIND THIS FILE:
//   - "One role per user per tenant" - the claims shape is a single role
//     string, never an array. isKnownRole validates exactly one value.
//   - "Scope comes from the token, not the UI" - every 'own' cell resolves
//     against the tenantId in the signed Identity Platform claim, never a
//     value the client sends. This file only names the levels; enforcement
//     sits with the route guards and the gateway middleware.
//   - FAIL CLOSED, ALWAYS: a missing, unknown, or malformed role resolves to
//     'none' everywhere. There is no default role and no anonymous access to
//     any RBAC-gated surface.

/** The seven role keys, exactly as the workbook's "Roles" tab spells them. */
export const ROLES = [
  'cw-platform-admin', // Platform Admin - YH staff who run the platform (all tenants)
  'cw-platform-ops', // Platform Ops - YH support and pipeline engineers (all tenants, read + repair)
  'lic-admin', // Licensor Admin - rights holder org admin (own library)
  'lic-approver', // Rights Approver - rights team reviewing AI-altered clips (own library)
  'lic-content', // Content Manager - ingest and metadata operators (own library)
  'buyer-admin', // Licensee Admin - buyer org admin (own organisation)
  'buyer-user', // Licensee User - creative/researcher searching and licensing (own organisation)
] as const;

export type Role = (typeof ROLES)[number];

/** The 36 permission keys, exactly as the workbook's "RBAC Matrix" tab spells them. */
export const PERMISSIONS = [
  // Platform & tenancy
  'tenant.provision',
  'platform.settings.write',
  'platform.dashboard.read',
  'audit.read',
  'platform.staff.manage',
  // Library configuration
  'licensor.profile.write',
  'ratecard.manage',
  'pricing.write',
  'pricing.defaults',
  'rights.manage',
  'library.share.grant',
  // Users and access
  'users.manage',
  'licensee.library.assign',
  // Content and ingest
  'asset.upload',
  'import.configure',
  'import.credentials.write',
  'import.backfill.start',
  'asset.metadata.write',
  'asset.reingest',
  'asset.delete',
  'catalog.manage',
  // Search and licensing
  'search.own',
  'search.cross',
  'preview.stream',
  'asset.download',
  'licence.purchase',
  'licence.void',
  'licence.read',
  'orders.support.read',
  // AI-altered submissions
  'submission.create',
  'submission.decide',
  'submission.override',
  'submission.lock.reset',
  // Billing and reporting
  'billing.own.read',
  'billing.platform.read',
  'billing.configure',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * The workbook legend, verbatim:
 *   full - "Full access across every tenant"
 *   own  - "Scoped to the user's own library or organisation, enforced by the
 *           tenantId claim"
 *   view - "Can see it, cannot change it"
 *   none - "The endpoint rejects the call and the UI does not render the
 *           control"
 */
export type AccessLevel = 'full' | 'own' | 'view' | 'none';

type MatrixRow = Record<Role, AccessLevel>;

const row = (
  platformAdmin: AccessLevel,
  platformOps: AccessLevel,
  licAdmin: AccessLevel,
  licApprover: AccessLevel,
  licContent: AccessLevel,
  buyerAdmin: AccessLevel,
  buyerUser: AccessLevel
): MatrixRow => ({
  'cw-platform-admin': platformAdmin,
  'cw-platform-ops': platformOps,
  'lic-admin': licAdmin,
  'lic-approver': licApprover,
  'lic-content': licContent,
  'buyer-admin': buyerAdmin,
  'buyer-user': buyerUser,
});

/**
 * The full role -> access matrix, one row per permission, columns in the
 * workbook's order: Platform Admin, Platform Ops, Licensor Admin, Rights
 * Approver, Content Manager, Licensee Admin, Licensee User.
 *
 * ONE CELL DELIBERATELY DIFFERS FROM THE SHEET: submission.override (Platform
 * Admin), per an explicit rule that supersedes the cell - see its comment.
 * asset.download originally carried a note-override too; that was reversed on
 * 2026-08-19 (grid over stale note) - see its comment.
 */
export const RBAC_MATRIX: Record<Permission, MatrixRow> = {
  // Platform & tenancy
  'tenant.provision': row('full', 'none', 'none', 'none', 'none', 'none', 'none'),
  'platform.settings.write': row('full', 'view', 'none', 'none', 'none', 'none', 'none'),
  'platform.dashboard.read': row('full', 'full', 'own', 'none', 'own', 'none', 'none'),
  'audit.read': row('full', 'view', 'own', 'none', 'none', 'own', 'none'),
  'platform.staff.manage': row('full', 'full', 'none', 'none', 'none', 'none', 'none'),
  // Library configuration
  'licensor.profile.write': row('full', 'none', 'own', 'none', 'none', 'none', 'none'),
  'ratecard.manage': row('full', 'view', 'own', 'view', 'none', 'view', 'view'),
  // Pricing writes, super-admin only at launch: Platform Admin 'full', every
  // other role 'none', matching tenant.provision. Two keys on purpose.
  // pricing.write covers owner rate cards and per-video overrides;
  // pricing.defaults covers the platform-wide Codeword baseline. requirePermission
  // does not distinguish 'full' from 'own' (rbac.middleware.ts), so one key on the
  // defaults route would admit a future own-scoped licensor admin. These two are
  // ahead of the workbook; the sheet is the source of record and is updated by hand.
  'pricing.write': row('full', 'none', 'none', 'none', 'none', 'none', 'none'),
  'pricing.defaults': row('full', 'none', 'none', 'none', 'none', 'none', 'none'),
  'rights.manage': row('full', 'view', 'own', 'view', 'view', 'view', 'view'),
  'library.share.grant': row('full', 'none', 'own', 'none', 'none', 'none', 'none'),
  // Users and access
  'users.manage': row('full', 'none', 'own', 'none', 'none', 'own', 'none'),
  'licensee.library.assign': row('full', 'full', 'none', 'none', 'none', 'none', 'none'),
  // Content and ingest
  // Both buyer cells 'none' on 2026-10-30 (Joseph Grafinger): buyers do not upload. No gateway route reads this. VPM
  // upload-api's own table decides, for a request with a verified token only; in its dual or header mode a request with
  // no token skips that table. The RBAC workbook is Vanja's and follows. submission.create is separate.
  'asset.upload': row('full', 'none', 'own', 'none', 'own', 'none', 'none'),
  'import.configure': row('full', 'view', 'own', 'none', 'own', 'none', 'none'),
  'import.credentials.write': row('full', 'none', 'own', 'none', 'own', 'none', 'none'),
  'import.backfill.start': row('full', 'none', 'own', 'none', 'none', 'none', 'none'),
  'asset.metadata.write': row('full', 'none', 'own', 'none', 'own', 'own', 'own'),
  'asset.reingest': row('full', 'full', 'own', 'none', 'own', 'none', 'none'),
  'asset.delete': row('full', 'none', 'own', 'none', 'none', 'none', 'none'),
  // The admin video manager (2026-10-26): list every tenant's catalogue, remove
  // and restore videos, read the removal audit. Platform Admin 'full', every
  // other role 'none', Platform Ops included, so requirePermission alone can
  // enforce it; the routes also carry requireFullAccess(). FULL-OR-NONE ON
  // PURPOSE: nothing filters the catalogue by tenant, so an 'own' or 'view'
  // cell here would hand that role every tenant's videos. asset.delete stays
  // as it is for a future licensor self-delete. Ahead of the workbook; the
  // sheet is the source of record and is updated by hand.
  'catalog.manage': row('full', 'none', 'none', 'none', 'none', 'none', 'none'),
  // Search and licensing
  'search.own': row('full', 'view', 'own', 'own', 'own', 'own', 'own'),
  'search.cross': row('full', 'view', 'none', 'none', 'none', 'own', 'own'),
  'preview.stream': row('full', 'full', 'own', 'own', 'own', 'own', 'own'),
  // DECISION REVERSAL (Joseph, 2026-08-19). Until this date the row note
  // ("Licensee roles: only assets covered by an active licence. Platform
  // roles: never.") overrode the sheet cell, and Platform Admin was encoded
  // 'none'. Reversed: the GRID cell (Platform Admin = Full) now wins and the
  // note is treated as stale - consistent with licence.purchase, which the
  // same matrix already grants Platform Admin with a stated rationale
  // ("Admin retains manual issue for offline / negotiated deals"). The
  // reversal names cw-platform-admin ONLY: Platform Ops keeps the note's
  // 'none', deliberately.
  'asset.download': row('full', 'none', 'own', 'none', 'own', 'own', 'own'),
  'licence.purchase': row('full', 'none', 'none', 'none', 'none', 'own', 'own'),
  'licence.void': row('full', 'none', 'own', 'none', 'none', 'none', 'none'),
  // Row note: "Licensee User sees their own licences; Licensee Admin sees the
  // whole organisation." That sub-scoping (own-licences vs own-organisation)
  // is finer than this matrix expresses; both are 'own' here, and the data
  // layer must apply the narrower filter when the surface is built.
  'licence.read': row('full', 'view', 'own', 'own', 'none', 'own', 'own'),
  // Reading ANY customer's orders, for support. Platform Admin only, decided by
  // Vanja 2026-09-09; Platform Ops was considered and refused.
  //
  // FULL-OR-NONE IS LOAD BEARING, for the same reason pricing.write is (see its
  // comment above). requirePermission admits 'full', 'own' and 'view' alike, so
  // any level other than 'none' on this key is a level the gateway cannot
  // enforce. licence.read was the obvious key to reuse and is the wrong one:
  // buyer-user holds it at 'own', so gating a cross-customer read on it would
  // hand every buyer every other buyer's purchase history. The only thing
  // standing between a signed-in user and that data is this row, because
  // req.rbac is not read for filtering anywhere (see 16.2 of the reference
  // document). Widening it to 'own' or 'view' is a mass data leak with no
  // second line of defence, which is what the full-or-none test in
  // rbac.spec.ts pins.
  'orders.support.read': row('full', 'none', 'none', 'none', 'none', 'none', 'none'),
  // AI-altered submissions
  'submission.create': row('full', 'none', 'none', 'none', 'none', 'own', 'own'),
  // Row note: "Owning licensor only. Comment is mandatory on reject and
  // request-changes." The mandatory-comment rule binds the surface
  // (modification.service.ts, E11); recorded here so the constants carry the
  // whole contract.
  //
  // RULE OVERRIDE (Joseph, 2026-10-19, decision J1 option b). The sheet cell for
  // Platform Admin reads Full. The row note reads "Owning licensor only", and
  // the Rules tab (quoted under submission.override below) says Platform Admin
  // "cannot approve, reject or reverse a decision". The cell contradicted both,
  // so cw-platform-admin is encoded 'none' here, deliberately: a decision is
  // contractual and is the owning licensor's alone. The gateway's shared access
  // check (modificationAccess.service.ts) refuses the admin independently of
  // this cell; the cell change removes the trap for the next route. The
  // workbook is the source of record and is to be updated to match (Vanja).
  'submission.decide': row('none', 'none', 'own', 'own', 'none', 'none', 'none'),
  // RULE OVERRIDE. The sheet cell for Platform Admin reads Full, but the row
  // note reads, verbatim: "Held by no role, by design. Decisions are
  // contractual and are bound to the licence." - and the Rules tab: "Nobody
  // overrides an approval. AI-altered submissions are decided by the owning
  // licensor alone. Platform Admin can reset a three-rejection lock or
  // unblock a stuck queue; it cannot approve, reject or reverse a decision."
  // All seven roles are therefore encoded as 'none', deliberately.
  'submission.override': row('none', 'none', 'none', 'none', 'none', 'none', 'none'),
  'submission.lock.reset': row('full', 'none', 'own', 'none', 'none', 'none', 'none'),
  // Billing and reporting
  'billing.own.read': row('full', 'none', 'own', 'none', 'none', 'own', 'none'),
  'billing.platform.read': row('full', 'view', 'none', 'none', 'none', 'none', 'none'),
  'billing.configure': row('full', 'none', 'none', 'none', 'none', 'none', 'none'),
};

/**
 * True only for one of the seven exact role strings. Everything else - null,
 * undefined, arrays, mixed case, unknown strings - is unknown, and unknown
 * means no access.
 */
export const isKnownRole = (value: unknown): value is Role =>
  typeof value === 'string' && (ROLES as readonly string[]).indexOf(value) !== -1;

/**
 * The access level a role holds on a permission. FAIL CLOSED: an unknown or
 * missing role, or a key that is not in the matrix, resolves to 'none'.
 */
export const getAccessLevel = (role: unknown, permission: Permission): AccessLevel => {
  if (!isKnownRole(role)) {
    return 'none';
  }

  const matrixRow = RBAC_MATRIX[permission];
  if (!matrixRow) {
    return 'none';
  }

  return matrixRow[role] ?? 'none';
};

/**
 * May this role SEE the surface at all? True for full, own, and view - the
 * read-render check. 'none' means "the endpoint rejects the call and the UI
 * does not render the control", per the legend.
 */
export const hasPermission = (role: unknown, permission: Permission): boolean =>
  getAccessLevel(role, permission) !== 'none';

/**
 * May this role CHANGE the thing? True for full and own only - 'view' can see
 * it, cannot change it.
 */
export const canWrite = (role: unknown, permission: Permission): boolean => {
  const level = getAccessLevel(role, permission);
  return level === 'full' || level === 'own';
};

/**
 * The two platform-tier roles. Everything the admin panel must not touch.
 *
 * The panel can neither grant these nor change anyone who currently holds one.
 * Both halves matter and they are not the same rule: without the second, an
 * ops or admin user can demote the last Platform Admin to buyer-user, which is
 * a grant of an allowed role and passes an allowlist that only checks the
 * incoming value. That leaves nobody holding pricing.write, pricing.defaults or
 * tenant.provision, and the panel cannot put it back. Two admins demoting each
 * other at the same moment reaches zero role-granters outright: the reads both
 * happen before either write, and custom claims have no compare-and-set.
 *
 * Platform roles are therefore stamped only by ops/scripts/set-rbac-claims.ts,
 * run by a human. That is the break-glass path and the bootstrap path for a
 * fresh environment, and it is why the script is retained rather than retired.
 */
export const PLATFORM_ROLES = ['cw-platform-admin', 'cw-platform-ops'] as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const isPlatformRole = (value: unknown): value is PlatformRole =>
  typeof value === 'string' && (PLATFORM_ROLES as readonly string[]).indexOf(value) !== -1;

/**
 * The roles the admin panel may grant. Derived, so a role added to ROLES cannot
 * be forgotten here, and pinned by a test, so one added to ROLES cannot become
 * grantable in silence.
 *
 * DERIVATION IS A DENYLIST IN DISGUISE, WHICH IS WHY THE TEST EXISTS. A new
 * role added to ROLES lands in this array automatically. If it is a new
 * platform-tier role and nobody adds it to PLATFORM_ROLES, the panel would
 * start granting it. The test in rbac.spec.ts pins this array to an explicit
 * literal, so adding a role turns it red and forces the decision instead.
 *
 * NOT COVERED BY RBAC_PARITY_SHA256. That hash serializes roles, permissions
 * and matrix cells only (see rbacChecksumInput below), so it would not notice
 * this array drifting between the repos. The pinning test is what covers it,
 * and it is duplicated in both copies of the spec for that reason.
 */
export const GRANTABLE_ROLES: readonly Role[] = ROLES.filter((role) => !isPlatformRole(role));

export const isGrantableRole = (value: unknown): value is Role =>
  typeof value === 'string' && (GRANTABLE_ROLES as readonly string[]).indexOf(value) !== -1;

/**
 * Canonical serialization for the cross-repo parity check. Deterministic by
 * construction: iterates PERMISSIONS and ROLES in their declared order, so
 * the same source text always produces the same string.
 */
export const rbacChecksumInput = (): string => {
  const cells = PERMISSIONS.map(
    (permission) =>
      permission + '=' + ROLES.map((role) => RBAC_MATRIX[permission][role]).join(',')
  );
  return (
    'roles:' + ROLES.join('|') + ';permissions:' + PERMISSIONS.join('|') + ';' + cells.join(';')
  );
};

/**
 * SHA-256 of rbacChecksumInput(), hardcoded IDENTICALLY in both repos' copies
 * of this file. The parity test recomputes the hash and asserts it matches.
 * If it fails: the two copies have drifted - diff them, re-sync BOTH to the
 * same content, and update this constant in BOTH in the same change.
 */
export const RBAC_PARITY_SHA256 = 'f91d2bc004988e0c319a630df37be43334993fd0dd0282bb3d200999ec24745f';
